import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Rating,
  Divider,
  IconButton,
  Badge,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Breadcrumbs,
  Link,
  Alert,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  ShoppingCart as CartIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  PhotoCamera as CameraIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Message as MessageIcon,
  Language as WebsiteIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerpointIcon,
  TextSnippet as TextIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from '../contexts/TranslationContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { UserService } from '../services/userService';
import { UserProductService, UserProduct } from '../services/userProductService';
import { ProfileImageService } from '../services/profileImageService';
import UnauthenticatedAccessDenied from '../components/Auth/UnauthenticatedAccessDenied';
import ProfileEditModal from '../components/Profile/ProfileEditModal';
import UserAvatar from '../components/Profile/UserAvatar';
import { supabase } from '../lib/supabase';
import { PlatformSettingsService, PlatformFeeSettings } from '../services/platformSettingsService';
import { calculatePriceWithFeesSync } from '../utils/priceUtils';
import toast from 'react-hot-toast';
import { translateText } from '../services/translationService';
import { useAppMode } from '../contexts/AppModeContext';

// Mock data for the user profile
const mockUser = {
  id: 'user1',
  name: 'John Smith',
  firstName: 'John',
  lastName: 'Smith',
  email: 'john.smith@email.com',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
  coverImage: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=400&fit=crop',
  verified: true,
  location: 'New York, NY',
  joinDate: '2023-01-15',
  totalOrders: 45,
  totalReviews: 12,
  rating: 4.8,
  reviewCount: 1250,
  totalSales: 1250,
  description: 'Passionate about sustainable fashion and quality products. I love discovering unique items and sharing my finds with the community.',
  stats: {
    totalProducts: 8,
    totalOrders: 45,
    repeatCustomers: 78,
    responseRate: 95,
  },
  categories: ['Clothing', 'Accessories', 'Electronics'],
  socialLinks: {
    website: 'https://johnsmith.com',
    instagram: '@johnsmith',
    twitter: '@johnsmith',
  },
};

const mockUserProducts = [
  {
    id: '1',
    title: 'Premium Cotton T-Shirt',
    price: 29.99,
    originalPrice: 39.99,
    image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop',
    category: 'Clothing',
    subcategory: 'Tops',
    brand: 'EcoWear',
    stock: 45,
    sold: 23,
    views: 156,
    rating: 4.5,
    reviewCount: 12,
    status: 'active',
    uploadDate: '2024-01-20',
    lastUpdated: '2024-01-25',
    tags: ['casual', 'cotton', 'comfortable'],
    color: ['Black', 'White', 'Navy'],
    size: ['S', 'M', 'L', 'XL'],
  },
  {
    id: '2',
    title: 'Designer Denim Jeans',
    price: 89.99,
    originalPrice: 119.99,
    image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=200&h=200&fit=crop',
    category: 'Clothing',
    subcategory: 'Bottoms',
    brand: 'DenimCo',
    stock: 28,
    sold: 15,
    views: 234,
    rating: 4.8,
    reviewCount: 8,
    status: 'active',
    uploadDate: '2024-01-18',
    lastUpdated: '2024-01-22',
    tags: ['denim', 'casual', 'premium'],
    color: ['Blue', 'Black'],
    size: ['28', '30', '32', '34', '36'],
  },
  {
    id: '3',
    title: 'Elegant Summer Dress',
    price: 65.99,
    originalPrice: 85.99,
    image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=200&h=200&fit=crop',
    category: 'Clothing',
    subcategory: 'Dresses',
    brand: 'SummerStyle',
    stock: 12,
    sold: 8,
    views: 189,
    rating: 4.6,
    reviewCount: 5,
    status: 'active',
    uploadDate: '2024-01-15',
    lastUpdated: '2024-01-20',
    tags: ['summer', 'elegant', 'floral'],
    color: ['Pink', 'Blue', 'White'],
    size: ['XS', 'S', 'M', 'L'],
  },
  {
    id: '4',
    title: 'Leather Crossbody Bag',
    price: 79.99,
    originalPrice: 99.99,
    image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=200&h=200&fit=crop',
    category: 'Accessories',
    subcategory: 'Bags',
    brand: 'LeatherLux',
    stock: 8,
    sold: 12,
    views: 298,
    rating: 4.9,
    reviewCount: 18,
    status: 'active',
    uploadDate: '2024-01-10',
    lastUpdated: '2024-01-18',
    tags: ['leather', 'luxury', 'handbag'],
    color: ['Brown', 'Black', 'Tan'],
    size: ['One Size'],
  },
];

const mockUserReviews = [
  {
    id: 1,
    user: {
      name: 'Sarah Johnson',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
      verified: true,
    },
    product: 'Premium Cotton T-Shirt',
    rating: 5,
    date: '2024-01-22',
    title: 'Perfect fit and quality!',
    comment: 'Love this t-shirt! The cotton is so soft and comfortable. The fit is exactly as described. Will definitely order more colors.',
    helpful: 8,
  },
  {
    id: 2,
    user: {
      name: 'Mike Chen',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dddc8c522?w=100&h=100&fit=crop&crop=face',
      verified: false,
    },
    product: 'Designer Denim Jeans',
    rating: 4,
    date: '2024-01-20',
    title: 'Great quality jeans',
    comment: 'Good quality denim, fits well. The shipping was fast and the seller was very responsive to my questions.',
    helpful: 5,
  },
  {
    id: 3,
    user: {
      name: 'Emily Davis',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      verified: true,
    },
    product: 'Elegant Summer Dress',
    rating: 5,
    date: '2024-01-18',
    title: 'Perfect dress and amazing seller!',
    comment: 'This dress is absolutely beautiful and the quality is outstanding. The seller was so helpful and even included a handwritten note. Will definitely shop here again!',
    helpful: 12,
  },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const UserProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { is_job } = useAppMode();
  const { id: userId } = useParams<{ id: string }>();
  const { t, language, autoTranslate } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [userData, setUserData] = useState(mockUser);
  
  // Real data states
  const [userAlias, setUserAlias] = useState<string | null>(null);
  const [userProducts, setUserProducts] = useState<UserProduct[]>([]);
  const [publicDocuments, setPublicDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [canViewProfile, setCanViewProfile] = useState<boolean | null>(null);
  const [accessChecked, setAccessChecked] = useState(false);
  const [platformFeeSettings, setPlatformFeeSettings] = useState<PlatformFeeSettings | null>(null);
  // Dynamic translations
  const [translatedProfile, setTranslatedProfile] = useState<{ description?: string; categories?: Record<string, string> }>({});
  const [translatedUserProducts, setTranslatedUserProducts] = useState<Record<string, { name?: string }>>({});

  // Resolve avatar and banner with fallbacks (ProfileImageService -> Supabase storage)
  const resolveProfileImages = async (uid: string) => {
    let avatarUrl: string | undefined;
    let bannerUrl: string | undefined;

    try {
      const imgs = await ProfileImageService.getUserProfileImages(uid);
      avatarUrl = imgs?.profile_picture_url || undefined;
      bannerUrl = imgs?.banner_image_url || undefined;
    } catch {}

    // Avatar fallback: Supabase storage bucket 'profile-pictures/{uid}/<latest>'
    if (!avatarUrl && uid) {
      try {
        const list = await supabase.storage
          .from('profile-pictures')
          .list(uid, { limit: 100, sortBy: { column: 'created_at', order: 'desc' } } as any);
        const files = (list?.data || []).filter((f: any) => !!f?.name);
        if (files.length > 0) {
          const latest = files[0].name;
          const { data: pub } = supabase.storage
            .from('profile-pictures')
            .getPublicUrl(`${uid}/${latest}`);
          avatarUrl = pub?.publicUrl || avatarUrl;
        }
      } catch {}
    }

    // Banner fallback: Supabase storage bucket 'profile-banners/{uid}/<latest>'
    if (!bannerUrl && uid) {
      try {
        const list = await supabase.storage
          .from('profile-banners')
          .list(uid, { limit: 100, sortBy: { column: 'created_at', order: 'desc' } } as any);
        const files = (list?.data || []).filter((f: any) => !!f?.name);
        if (files.length > 0) {
          const latest = files[0].name;
          const { data: pub } = supabase.storage
            .from('profile-banners')
            .getPublicUrl(`${uid}/${latest}`);
          bannerUrl = pub?.publicUrl || bannerUrl;
        }
      } catch {}
    }

    return { avatarUrl, bannerUrl };
  };

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      // If no userId provided, show current user's profile
      const targetUserId = userId || currentUser?.id;
      
      if (!targetUserId) {
        setError('No user ID provided and no current user');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if this is the current user's own profile
        const isOwn = currentUser?.id === targetUserId;
        setIsOwnProfile(isOwn);

        if (isOwn) {
          // Fetch current user's data
          const [alias, products, profileImages] = await Promise.all([
            UserService.getCurrentUserAlias(currentUser.id),
            UserProductService.getUserProducts(),
            ProfileImageService.getUserProfileImages(currentUser.id)
          ]);

          setUserAlias(alias);
          setUserProducts(products);
          setCanViewProfile(true); // Own profile is always viewable

          // Update userData with real data
          try {
            const fb = await resolveProfileImages(currentUser.id);
            setUserData(prev => ({
              ...prev,
              name: alias || currentUser.email || prev.name,
              firstName: prev.firstName,
              lastName: prev.lastName,
              id: currentUser.id,
              email: currentUser.email,
              avatar: profileImages?.profile_picture_url || fb.avatarUrl || currentUser.avatar || prev.avatar,
              coverImage: profileImages?.banner_image_url || fb.bannerUrl || prev.coverImage,
              joinDate: (currentUser as any)?.createdAt || prev.joinDate,
              workExperience: '',
              education: '',
            }));
          } catch {
            setUserData(prev => ({
              ...prev,
              name: alias || currentUser.email || prev.name,
              firstName: prev.firstName,
              lastName: prev.lastName,
              id: currentUser.id,
              email: currentUser.email,
              avatar: profileImages?.profile_picture_url || currentUser.avatar || prev.avatar,
              coverImage: profileImages?.banner_image_url || prev.coverImage,
              joinDate: (currentUser as any)?.createdAt || prev.joinDate,
              workExperience: '',
              education: '',
            }));
          }
        } else {
          // Fetch other user's data
          console.log('UserProfilePage: Fetching user data for ID:', targetUserId);
          
          const { data: userProfile, error: userError } = await supabase
            .from('users')
            .select('id, alias, is_public')
            .eq('id', targetUserId)
            .single();

          console.log('UserProfilePage: User profile data:', userProfile, 'Error:', userError);

          if (userError) {
            console.error('UserProfilePage: Error fetching user profile:', userError);
            if (userError.code === 'PGRST116') {
              throw new Error('User not found or access denied');
            } else {
              throw new Error(`Failed to fetch user: ${userError.message}`);
            }
          }

          if (!userProfile) {
            throw new Error('User not found');
          }

          // Check if user can view this profile
          if (!currentUser?.id) {
            // Unauthenticated users can only view public profiles
            setCanViewProfile(userProfile.is_public ?? false);
          } else {
            // Authenticated users - for now, allow viewing (can be enhanced with inquiry system)
            setCanViewProfile(true);
          }

          if (userProfile) {
            console.log('UserProfilePage: Found user profile:', userProfile);
            setUserAlias(userProfile.alias);
            setUserData(prev => ({
              ...prev,
              name: userProfile.alias || `User_${userProfile.id.substring(0, 8)}`,
              firstName: (userProfile as any).first_name || prev.firstName,
              lastName: (userProfile as any).last_name || prev.lastName,
              id: userProfile.id,
              avatar: prev.avatar, // temp until we resolve below
              joinDate: prev.joinDate, // Keep the default join date from mockUser
              workExperience: (userProfile as any).work_experience || '',
              education: (userProfile as any).education || '',
            }));

            // Resolve this user's profile images with fallback
            try {
              const fb = await resolveProfileImages(targetUserId);
              setUserData(prev => ({
                ...prev,
                avatar: fb.avatarUrl || prev.avatar,
                coverImage: fb.bannerUrl || prev.coverImage,
              }));
            } catch {}

            // Fetch other user's products
            console.log('UserProfilePage: Fetching products for user:', targetUserId);
            
            // First, let's test if there are any products at all
            const { data: allProducts, error: allProductsError } = await supabase
              .from('products')
              .select('id, seller_id, name')
              .limit(5);
            
            console.log('UserProfilePage: Sample products in database:', allProducts, 'Error:', allProductsError);
            
            // Now fetch products for this specific user
            const { data: products, error: productsError } = await supabase
              .from('products')
              .select('*')
              .eq('seller_id', targetUserId)
              .order('created_at', { ascending: false });

            console.log('UserProfilePage: Products for user', targetUserId, ':', products, 'Error:', productsError);

            if (productsError) {
              console.error('UserProfilePage: Error fetching products:', productsError);
              // Don't throw error for products, just log it and continue with empty array
              setUserProducts([]);
            } else if (products && products.length > 0) {
              console.log('UserProfilePage: Found products:', products.length);
              setUserProducts(products.map(product => ({
                id: product.id,
                name: product.name,
                price: product.price,
                image: product.images && product.images.length > 0 ? product.images[0] : '/placeholder-product.jpg',
                description: product.description,
                category_name: product.category || 'Unknown',
                subcategory: product.subcategory,
                condition: product.condition,
                brand: product.brand,
                size: product.size,
                color: product.color,
                material: product.material,
                is_active: product.is_active,
                is_featured: product.is_featured,
                is_sold: product.is_sold,
                number_sold: product.number_sold || 0,
                created_at: product.created_at,
                updated_at: product.updated_at,
                // Add missing required fields with safe defaults
                category_id: product.category_id || '',
                subcategory_id: product.subcategory_id || '',
                images: product.images || [],
                colors: product.colors || [],
                sizes: product.sizes || [],
                stock: product.stock || 0,
                sold: product.sold || 0,
                views: product.views || 0,
                seller_id: product.seller_id,
                seller: null,
                custom_colors: product.custom_colors || [],
                size_system: product.size_system || '',
                tags: product.tags || [],
                view_count: product.view_count || 0,
                favorite_count: product.favorite_count || 0,
              })));
            } else {
              console.log('UserProfilePage: No products found for user:', targetUserId);
              setUserProducts([]);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user data');
        setCanViewProfile(false);
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
        setAccessChecked(true);
      }
    };

    fetchUserData();
  }, [userId, currentUser]);

  // Load platform fee settings on mount
  useEffect(() => {
    const loadFeeSettings = async () => {
      try {
        const settings = await PlatformSettingsService.getPlatformFeeSettings();
        setPlatformFeeSettings(settings);
      } catch (error) {
        console.error('Error loading platform fee settings:', error);
        // Set default fee settings if loading fails
        setPlatformFeeSettings({
          fee_type: 'fixed',
          fee_amount: 0.50,
          fee_percentage: null,
        });
      }
    };
    loadFeeSettings();
  }, []);

  // Translate profile description and interests
  useEffect(() => {
    const run = async () => {
      try {
        if (!userData) return;
        if (!autoTranslate || language === 'en') {
          setTranslatedProfile({});
          return;
        }
        const texts = new Set<string>();
        if (userData.description) texts.add(userData.description);
        (userData.categories || []).forEach((c: string) => texts.add(c));
        const mapText: Record<string, string> = {};
        await Promise.all(Array.from(texts).map(async (txt) => {
          const tr = await translateText(txt, language as any, 'en');
          mapText[txt] = tr;
        }));
        const cats: Record<string, string> = {};
        (userData.categories || []).forEach((c: string) => {
          cats[c] = mapText[c] || c;
        });
        setTranslatedProfile({
          description: userData.description ? (mapText[userData.description] || userData.description) : undefined,
          categories: cats,
        });
      } catch {
        // silent fallback
      }
    };
    run();
  }, [userData, language, autoTranslate]);

  // Translate product titles for user's products
  useEffect(() => {
    const run = async () => {
      try {
        if (!autoTranslate || language === 'en') {
          setTranslatedUserProducts({});
          return;
        }
        if (!userProducts || userProducts.length === 0) {
          setTranslatedUserProducts({});
          return;
        }
        const texts = new Set<string>();
        userProducts.forEach(p => { if (p.name) texts.add(p.name); });
        const mapText: Record<string, string> = {};
        await Promise.all(Array.from(texts).map(async (txt) => {
          const tr = await translateText(txt, language as any, 'en');
          mapText[txt] = tr;
        }));
        const out: Record<string, { name?: string }> = {};
        userProducts.forEach(p => {
          out[p.id] = { name: p.name ? (mapText[p.name] || p.name) : undefined };
        });
        setTranslatedUserProducts(out);
      } catch {
        // silent fallback
      }
    };
    run();
  }, [userProducts, language, autoTranslate]);

  // Fetch public documents for the user
  useEffect(() => {
    const fetchPublicDocuments = async () => {
      const targetUserId = userId || currentUser?.id;
      if (!targetUserId) return;

      setDocumentsLoading(true);
      try {
        const { data: documents, error } = await supabase
          .from('documents')
          .select('*')
          .eq('user_id', targetUserId)
          .eq('is_public', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching public documents:', error);
          setPublicDocuments([]);
        } else {
          setPublicDocuments(documents || []);
        }
      } catch (err) {
        console.error('Error fetching public documents:', err);
        setPublicDocuments([]);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchPublicDocuments();
  }, [userId, currentUser]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditProfile = () => {
    if (isOwnProfile) {
      setEditModalOpen(true);
    }
  };

  const handleSaveProfile = async (updatedData: any) => {
    // Merge the updated data with the existing userData to preserve stats and other properties
    const mergedData = {
      ...userData,
      name: `${updatedData.firstName} ${updatedData.lastName}`.trim(),
      firstName: updatedData.firstName,
      lastName: updatedData.lastName,
      email: updatedData.email,
      location: updatedData.location,
      description: updatedData.bio,
      avatar: updatedData.avatar,
      coverImage: updatedData.coverImage,
      categories: updatedData.interests,
      socialLinks: {
        ...userData.socialLinks,
        website: updatedData.website,
      },
      workExperience: updatedData.workExperience,
      education: updatedData.education,
    };
    setUserData(mergedData);
    setEditModalOpen(false);
    
    // Show success message
    toast.success('Profile updated successfully!');
  };

  const handleImageDeleted = (type: 'avatar' | 'banner') => {
    if (type === 'avatar') {
      setUserData(prev => ({
        ...prev,
        avatar: ''
      }));
    } else if (type === 'banner') {
      setUserData(prev => ({
        ...prev,
        coverImage: ''
      }));
    }
  };

  // Document helper functions
  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <PdfIcon sx={{ color: '#d32f2f', fontSize: 24 }} />;
    if (fileType.includes('image')) return <ImageIcon sx={{ color: '#1976d2', fontSize: 24 }} />;
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <ExcelIcon sx={{ color: '#2e7d32', fontSize: 24 }} />;
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return <PowerpointIcon sx={{ color: '#f57c00', fontSize: 24 }} />;
    if (fileType.includes('text')) return <TextIcon sx={{ color: '#424242', fontSize: 24 }} />;
    return <DocumentIcon sx={{ color: '#666', fontSize: 24 }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (documentType: string) => {
    const typeMap: { [key: string]: string } = {
      'cv': 'CV/Resume',
      'cover_letter': 'Cover Letter',
      'certificate': 'Certificate',
      'diploma': 'Diploma',
      'portfolio': 'Portfolio',
      'other': 'Other',
      'employment_document': 'Employment Document',
      'license': 'License',
      'contract': 'Contract'
    };
    return typeMap[documentType] || documentType;
  };

  const handleDownloadDocument = async (document: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('user-documents')
        .download(document.file_path);

      if (error) {
        console.error('Error downloading document:', error);
        toast.error('Failed to download document');
        return;
      }

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = document.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Document downloaded successfully!');
    } catch (err) {
      console.error('Error downloading document:', err);
      toast.error('Failed to download document');
    }
  };

  const handleViewDocument = async (document: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('user-documents')
        .createSignedUrl(document.file_path, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error('Failed to open document');
        return;
      }

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
      toast.error('Failed to open document');
    }
  };

  const renderUserHeader = () => (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1, overflow: 'hidden' }}>
      <Box sx={{ position: 'relative' }}>
        {/* Cover Image */}
        <Box
          sx={{
            height: { xs: 200, md: 250 },
            backgroundImage: `url(${userData.coverImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            overflow: 'hidden',
            cursor: isOwnProfile ? 'pointer' : 'default',
            '&:hover .cover-overlay': {
              opacity: isOwnProfile ? 1 : 0,
            },
          }}
          onClick={isOwnProfile ? handleEditProfile : undefined}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
            }}
          />
          <Box
            className="cover-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.3s',
            }}
          >
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              <CameraIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6" fontWeight="bold">
                {t('userProfile.cover.edit')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* User Info Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: -40, md: -50 },
            left: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'center', md: 'flex-end' },
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              cursor: isOwnProfile ? 'pointer' : 'default',
              '&:hover .avatar-overlay': {
                opacity: isOwnProfile ? 1 : 0,
              },
            }}
            onClick={isOwnProfile ? handleEditProfile : undefined}
          >
            <UserAvatar
              src={userData.avatar}
              name={userData.name}
              email={userData.email}
              size={100}
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                border: '4px solid white',
                boxShadow: 3,
              }}
            />
            <Box
              className="avatar-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '50%',
                bgcolor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.3s',
              }}
            >
              <CameraIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
          </Box>
          
          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                sx={{ 
                  color: '#1a1a1a',
                  textShadow: '1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)',
                  filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.6))'
                }}
              >
                {loading ? (
                  <Skeleton variant="text" width={200} height={40} />
                ) : userAlias ? (
                  `@${userAlias}`
                ) : (
                  userData.name
                )}
              </Typography>
              {userData.verified && (
                <VerifiedIcon 
                  sx={{ 
                    color: 'primary.main', 
                    fontSize: 28,
                    filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.6))'
                  }} 
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <Rating 
              value={userData.rating || 0} 
              precision={0.1} 
              size="large" 
              readOnly 
              sx={{
                '& .MuiRating-icon': {
                  filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))',
                  textShadow: '1px 1px 2px rgba(255,255,255,0.8)'
                },
                '& .MuiRating-iconEmpty': {
                  color: 'rgba(255,255,255,0.3)',
                  filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.4))'
                }
              }}
            />
            <Typography 
              variant="h6" 
              fontWeight="bold"
              sx={{ 
                color: '#1a1a1a',
                textShadow: '1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)'
              }}
            >
              {userData.rating || 0} ({userData.reviewCount || 0} {t('userProfile.reviews')})
            </Typography>
            </Box>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#2a2a2a',
                textShadow: '1px 1px 2px rgba(255,255,255,0.7), -1px -1px 2px rgba(255,255,255,0.7)',
                fontWeight: 500
              }}
            >
              {(userData.location || t('userProfile.location.unknown'))} • {t('userProfile.memberSince').replace('{year}', String(userData.joinDate ? new Date(userData.joinDate).getFullYear() : new Date().getFullYear()))}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'row', md: 'column' } }}>
            {isOwnProfile ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  boxShadow: 3,
                  '&:hover': { 
                    bgcolor: 'primary.dark',
                    boxShadow: 4,
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={handleEditProfile}
              >
                {t('userProfile.buttons.editProfile')}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<MessageIcon />}
                sx={{ 
                  bgcolor: 'secondary.main',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  boxShadow: 3,
                  '&:hover': { 
                    bgcolor: 'secondary.dark',
                    boxShadow: 4,
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={() => navigate('/messages')}
              >
                {t('userProfile.buttons.sendMessage')}
              </Button>
            )}
            <IconButton
              sx={{ 
                bgcolor: 'white',
                color: 'text.secondary',
                boxShadow: 2,
                width: 48,
                height: 48,
                borderRadius: '50%',
                '&:hover': { 
                  bgcolor: 'grey.100',
                  boxShadow: 3,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <ShareIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
      
      {/* Spacer to prevent content from being cut off */}
      <Box sx={{ height: { xs: 60, md: 80 } }} />
    </Card>
  );

  const renderUserStats = () => (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          {t('userProfile.stats.title')}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ textAlign: 'center', flex: '1 1 150px' }}>
            {loading ? (
              <Skeleton variant="text" width={60} height={40} />
            ) : (
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {userProducts.length}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('userProfile.stats.productsListed')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: '1 1 150px' }}>
            {loading ? (
              <Skeleton variant="text" width={60} height={40} />
            ) : (
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {userProducts.reduce((sum, p) => sum + (p.sold || 0), 0)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('userProfile.stats.totalSold')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: '1 1 150px' }}>
            {loading ? (
              <Skeleton variant="text" width={60} height={40} />
            ) : (
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {userProducts.reduce((sum, p) => sum + (p.view_count || 0), 0)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('userProfile.stats.totalViews')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: '1 1 150px' }}>
            {loading ? (
              <Skeleton variant="text" width={60} height={40} />
            ) : (
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {userProducts.length > 0 ? Math.round(userProducts.reduce((sum, p) => sum + (p.rating || 0), 0) / userProducts.length * 10) / 10 : 0}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('userProfile.stats.averageRating')}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderUserInfo = () => (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          {t('userProfile.about.title').replace('{name}', userAlias ? `@${userAlias}` : (userData.name || ''))}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
          {translatedProfile.description || userData.description || t('userProfile.about.noDescription')}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              {userData.location || t('userProfile.location.notSpecified')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              {t('userProfile.joinedOn').replace('{date}', userData.joinDate ? new Date(userData.joinDate).toLocaleDateString(language) : new Date().toLocaleDateString(language))}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              {userData.email || t('userProfile.email.notProvided')}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('userProfile.interests.title')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(userData.categories || []).map((category, index) => (
              <Chip
                key={index}
                label={(translatedProfile.categories && translatedProfile.categories[category]) || category}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const [translatedReviews, setTranslatedReviews] = useState<Record<number, { title?: string; comment?: string; product?: string }>>({});

  useEffect(() => {
    const run = async () => {
      try {
        if (!autoTranslate || language === 'en') {
          setTranslatedReviews({});
          return;
        }
        const texts = new Set<string>();
        mockUserReviews.forEach(r => {
          if (r.title) texts.add(r.title);
          if (r.comment) texts.add(r.comment);
          if (r.product) texts.add(r.product);
        });
        const mapText: Record<string, string> = {};
        await Promise.all(Array.from(texts).map(async (txt) => {
          const tr = await translateText(txt, language as any, 'en');
          mapText[txt] = tr;
        }));
        const out: Record<number, { title?: string; comment?: string; product?: string }> = {};
        mockUserReviews.forEach(r => {
          out[r.id] = {
            title: r.title ? (mapText[r.title] || r.title) : undefined,
            comment: r.comment ? (mapText[r.comment] || r.comment) : undefined,
            product: r.product ? (mapText[r.product] || r.product) : undefined,
          };
        });
        setTranslatedReviews(out);
      } catch {
        // silent
      }
    };
    run();
  }, [language, autoTranslate]);

  const renderReviews = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
        {t('userProfile.reviews.title')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {mockUserReviews.map((review, index) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <UserAvatar 
                    src={review.user.avatar} 
                    name={review.user.name}
                    size={48}
                    sx={{ width: 48, height: 48 }} 
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {review.user.name}
                      </Typography>
                      {review.user.verified && (
                        <VerifiedIcon color="primary" sx={{ fontSize: 16 }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Rating value={review.rating} size="small" readOnly />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(review.date).toLocaleDateString(language)}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {translatedReviews[review.id]?.title || review.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {translatedReviews[review.id]?.comment || review.comment}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('userProfile.reviews.productPrefix')} {translatedReviews[review.id]?.product || review.product}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                        <ThumbUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {t('userProfile.reviews.helpful').replace('{count}', String(review.helpful))}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>
    </Box>
  );

  const renderDocuments = () => (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <DocumentIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {t('userProfile.documents.title')}
          </Typography>
          <PublicIcon sx={{ color: 'success.main', fontSize: 20 }} />
        </Box>
        
        {documentsLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
                <Skeleton variant="rectangular" width={80} height={32} />
              </Box>
            ))}
          </Box>
        ) : publicDocuments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('userProfile.documents.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isOwnProfile 
                ? t('userProfile.documents.empty.subtitleSelf')
                : t('userProfile.documents.empty.subtitleOther')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {publicDocuments.map((document, index) => (
              <motion.div
                key={document.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 1,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48 }}>
                    {getFileIcon(document.file_type)}
                  </Box>
                  
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                      {document.file_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={getDocumentTypeLabel(document.document_type)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', height: 20 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(document.file_size)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(document.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {document.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                        {document.description}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewDocument(document)}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                      }}
                    >
                      {t('userProfile.buttons.view')}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadDocument(document)}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        bgcolor: 'success.main',
                        '&:hover': {
                          bgcolor: 'success.dark',
                        },
                      }}
                    >
                      {t('userProfile.buttons.download')}
                    </Button>
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Show loading state while checking access
  if (!accessChecked || loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('userProfile.loading.checkingAccess')}
          </Typography>
        </Box>
      </Box>
    );
  }

  // If user is not authenticated and profile is private, show access denied
  if (!currentUser?.id && canViewProfile === false) {
    return <UnauthenticatedAccessDenied />;
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            {t('common.goBack') || 'Go Back'}
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', boxShadow: 1, position: 'sticky', top: 0, zIndex: 1000 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <IconButton onClick={() => navigate(-1)}>
              <BackIcon />
            </IconButton>
            <Breadcrumbs>
              <Link
                component="button"
                variant="body1"
                onClick={() => navigate('/')}
                sx={{ textDecoration: 'none' }}
              >
                {t('userProfile.breadcrumb.home')}
              </Link>
              <Typography color="text.primary">{t('userProfile.breadcrumb.profile')}</Typography>
            </Breadcrumbs>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* User Header */}
        {renderUserHeader()}

        {/* User Stats */}
        {renderUserStats()}

        {/* User Info */}
        {renderUserInfo()}

        {/* Documents Section (job mode only) */}
        {is_job && renderDocuments()}

        {/* Products Section */}
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {isOwnProfile ? t('userProfile.products.title.mine') : t('userProfile.products.title.user').replace('{name}', userAlias || 'User')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {isOwnProfile 
                    ? t('userProfile.products.manageListed').replace('{count}', String(loading ? '...' : userProducts.length))
                    : t('userProfile.products.userListedCount').replace('{name}', userAlias || 'User').replace('{count}', String(loading ? '...' : userProducts.length))
                  }
                </Typography>
              </Box>
              {isOwnProfile && (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<StoreIcon />}
                  sx={{ px: 3, py: 1.5 }}
                  onClick={() => navigate('/upload-product')}
                >
                  {t('userProfile.buttons.addNewProduct')}
                </Button>
              )}
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {[1, 2, 3, 4].map((index) => (
                  <Card key={index} sx={{ width: 280, borderRadius: 2, boxShadow: 1 }}>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent>
                      <Skeleton variant="text" height={32} />
                      <Skeleton variant="text" height={24} width="60%" />
                      <Skeleton variant="text" height={20} width="80%" />
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            ) : userProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('userProfile.products.empty.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {isOwnProfile 
                    ? t('userProfile.products.empty.subtitleSelf')
                    : t('userProfile.products.empty.subtitleOther')
                  }
                </Typography>
                {isOwnProfile && (
                  <Button
                    variant="contained"
                    startIcon={<StoreIcon />}
                    onClick={() => navigate('/upload-product')}
                    sx={{ px: 4 }}
                  >
                    {t('userProfile.buttons.uploadProduct')}
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {userProducts.slice(0, 4).map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      width: 280,
                      borderRadius: 2,
                      boxShadow: 1,
                      cursor: 'pointer',
                      '&:hover': { 
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease-in-out'
                      },
                    }}
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={product.images && product.images.length > 0 ? product.images[0] : '/placeholder-product.jpg'}
                        alt={product.name}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: '8px 8px 0 0',
                        }}
                      />
                      {product.original_price && (
                        <Chip
                          label={`${Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF`}
                          color="error"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </Box>
                    
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {translatedUserProducts[product.id]?.name || product.name}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {(() => {
                          // Calculate price with fees (only for products, not jobs)
                          const basePrice = product.price || 0;
                          const displayPrice = platformFeeSettings
                            ? calculatePriceWithFeesSync(basePrice, platformFeeSettings)
                            : basePrice;
                          const baseOriginalPrice = product.original_price;
                          const displayOriginalPrice = baseOriginalPrice && platformFeeSettings
                            ? calculatePriceWithFeesSync(baseOriginalPrice, platformFeeSettings)
                            : baseOriginalPrice;
                          
                          return (
                            <>
                              <Typography variant="h6" color="primary.main" fontWeight="bold">
                                ${displayPrice.toFixed(2)}
                              </Typography>
                              {displayOriginalPrice && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textDecoration: 'line-through' }}
                                >
                                  ${displayOriginalPrice.toFixed(2)}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Rating value={4.0} precision={0.1} size="small" readOnly />
                        <Typography variant="body2" color="text.secondary">
                          ({product.view_count || 0})
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip
                          label={t('userProfile.sold.count').replace('{count}', String(product.number_sold || 0))}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          label={product.category_name || 'Unknown'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('userProfile.stock.inStock').replace('{count}', String(product.stock || 0))}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {product.tags && product.tags.slice(0, 2).map((tag, idx) => (
                            <Chip
                              key={idx}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </Box>

            {userProducts.length > 4 && isOwnProfile && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<StoreIcon />}
                  sx={{ px: 4, py: 1.5 }}
                  onClick={() => navigate('/dashboard')}
                >
                  {t('userProfile.buttons.viewAllMyProducts')}
                </Button>
              </Box>
            )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Tabs Section */}
        <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t('userProfile.tabs.reviews')} />
            <Tab label={t('userProfile.tabs.about')} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderReviews()}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Contact Information
                </Typography>
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={mockUser.email} />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <LocationIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={mockUser.location} />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CalendarIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={`Joined ${new Date(mockUser.joinDate).toLocaleDateString()}`} />
                  </ListItem>
                </List>
              </Box>

              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Interests & Categories
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {mockUser.categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={category}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </TabPanel>
        </Card>
      </Container>

      {/* Profile Edit Modal */}
      <ProfileEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveProfile}
        onImageDeleted={handleImageDeleted}
        initialData={{
          firstName: userData.firstName || userData.name?.split(' ')[0] || '',
          lastName: userData.lastName || userData.name?.split(' ').slice(1).join(' ') || '',
          email: userData.email,
          location: userData.location,
          bio: userData.description,
          website: userData.socialLinks?.website || '',
          avatar: userData.avatar,
          coverImage: userData.coverImage,
          interests: userData.categories,
          isPublic: true,
          notifications: true,
          socialMediaLinks: userData.socialLinks || {},
          selectedAddressId: undefined,
          workExperience: '',
          education: '',
        }}
      />
    </Box>
  );
};

export default UserProfilePage;
